/**
 * The PIN keypad (FM Technician rebuild B17, C21; D24 — one of the new UI
 * primitives: none existed).
 *
 * Big keys for a gloved thumb: every key is 56px tall (above the .tech 48px
 * floor), digits at 24px. Four dots show how many digits are in, never the
 * digits. A physical keyboard works too (digits, Backspace). The parent owns
 * the value; `onComplete` fires once the last digit is in.
 */
import { useEffect, useRef } from 'react';
import { BackspaceIcon } from '@heroicons/react/24/outline';

export interface KeypadProps {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (pin: string) => void;
  length?: number;
  /** What the dots are for ("New PIN", "Enter your PIN"). */
  label: string;
  disabled?: boolean;
  /** A message under the dots (a refusal, tries left). */
  error?: string | null;
  testid?: string;
}

/**
 * Review R3-4: both pads listen on `window`, so a pad the user cannot see
 * must not take the keys. While the PIN lock is up the app shell stays
 * mounted but hidden (display:none + inert, AppLayout) — a reading's
 * NumberPad or the Profile page's PIN setter in it would otherwise take the
 * unlock PIN as typed into them too. True when the pad is inside `[inert]`
 * or is not rendered (display:none on it or an ancestor, `hidden`).
 */
export function padIsHidden(el: HTMLElement | null): boolean {
  if (!el || !el.isConnected) return true;
  if (el.closest('[inert]')) return true;
  const check = (el as HTMLElement & { checkVisibility?: () => boolean }).checkVisibility;
  if (typeof check === 'function') return !check.call(el);
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    if (n.hidden || window.getComputedStyle(n).display === 'none') return true;
  }
  return false;
}

/**
 * Review R3-4: a focused control keeps Enter — a link, a button, a field, a
 * summary, anything focusable by tab — so the NumberPad's "Enter is Done"
 * never swallows the Enter that would follow a link or press a button.
 */
const INTERACTIVE = 'a,button,input,select,textarea,[role="button"],[role="link"],summary,[contenteditable="true"],[tabindex]:not([tabindex="-1"])';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'] as const;

export default function Keypad({
  value, onChange, onComplete, length = 4, label, disabled = false, error, testid = 'keypad',
}: KeypadProps) {
  const root = useRef<HTMLDivElement>(null);
  const press = (key: (typeof KEYS)[number]) => {
    if (disabled) return;
    if (key === 'back') { onChange(value.slice(0, -1)); return; }
    if (key === 'clear') { onChange(''); return; }
    if (value.length >= length) return;
    const next = value + key;
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (padIsHidden(root.current)) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (/^[0-9]$/.test(e.key)) { e.preventDefault(); press(e.key as (typeof KEYS)[number]); }
      else if (e.key === 'Backspace') { e.preventDefault(); press('back'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // press reads the latest value through the closure of this render.
  });

  return (
    <div ref={root} className="mx-auto w-full max-w-xs space-y-4" data-testid={testid}>
      <div role="status" aria-label={`${label}: ${value.length} of ${length} digits entered`}
           className="flex justify-center gap-4" data-testid={`${testid}-dots`}>
        {Array.from({ length }, (_, i) => (
          <span key={i} aria-hidden="true"
                className={`h-4 w-4 rounded-full border-2 ${i < value.length
                  ? 'border-brand-700 bg-brand-700' : 'border-content-subtle bg-transparent'}`} />
        ))}
      </div>
      <p className="text-center text-base font-medium text-content">{label}</p>
      {error && (
        <p role="alert" className="text-center text-base text-danger-700" data-testid={`${testid}-error`}>{error}</p>
      )}
      <div className="grid grid-cols-3 gap-3" role="group" aria-label={label}>
        {KEYS.map((k) => {
          const isDigit = /^[0-9]$/.test(k);
          const aria = k === 'back' ? 'Delete last digit' : k === 'clear' ? 'Clear' : k;
          return (
            <button key={k} type="button" disabled={disabled} aria-label={aria}
                    data-testid={`${testid}-key-${k}`}
                    onClick={() => press(k)}
                    className={`flex h-14 min-h-[3.5rem] items-center justify-center rounded-xl border border-line text-2xl font-semibold text-content
                      ${isDigit ? 'bg-surface hover:bg-surface-subtle' : 'bg-surface-subtle text-base hover:bg-surface-muted'}
                      disabled:cursor-not-allowed disabled:opacity-50`}>
              {k === 'back' ? <BackspaceIcon className="h-7 w-7" aria-hidden="true" /> : k === 'clear' ? 'Clear' : k}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NumberPad — the same big keys for a meter or gauge reading (C10, B30;
// DESIGN-CHECK §2 "Keypad: big 48px+ digit keys, mono display, decimal,
// minus, backspace"). The value is shown, never hidden.
// ---------------------------------------------------------------------------

export interface NumberPadProps {
  value: string;
  onChange: (next: string) => void;
  /** What is being read ("Coolant temp (on load), °C"). */
  label: string;
  /** Under the display: the normal range, "Out of range"… */
  hint?: string | null;
  bad?: boolean;
  onDone?: () => void;
  testid?: string;
}

const NUM_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'] as const;

/** Appends a key to a typed number: one decimal point, a minus only first. */
export function pressNumber(value: string, key: string): string {
  if (key === 'back') return value.slice(0, -1);
  if (key === 'clear') return '';
  if (key === '-') return value.startsWith('-') ? value.slice(1) : `-${value}`;
  if (key === '.') return value.includes('.') ? value : `${value || '0'}.`;
  if (value.replace(/^-/, '').replace('.', '').length >= 12) return value;
  return value === '0' ? key : value === '-0' ? `-${key}` : value + key;
}

export function NumberPad({ value, onChange, label, hint, bad = false, onDone, testid = 'numpad' }: NumberPadProps) {
  // A physical keyboard works while the pad is open, as on the PIN Keypad:
  // digits, ".", "-", Backspace, and Enter for Done. Not while typing in a
  // field (the pad's own keys and inputs elsewhere keep their keys), and not
  // while the pad is hidden — under the PIN lock (review R3-4).
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (padIsHidden(root.current)) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      let key: string | null = null;
      if (/^[0-9]$/.test(e.key)) key = e.key;
      else if (e.key === '.') key = '.';
      else if (e.key === '-') key = '-';
      else if (e.key === 'Backspace') key = 'back';
      if (key) { e.preventDefault(); onChange(pressNumber(value, key)); return; }
      // Enter on a focused control (a button, a link…) is that control's;
      // elsewhere it is Done (review R3-4: only BUTTON was left alone).
      if (e.key === 'Enter' && onDone && !(t && typeof t.closest === 'function' && t.closest(INTERACTIVE))) {
        e.preventDefault(); onDone();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // Reads the latest value through the closure of this render.
  });
  return (
    <div ref={root} className="mx-auto w-full max-w-xs space-y-3" data-testid={testid}>
      <p className="text-base font-medium text-content">{label}</p>
      <output aria-live="polite" data-testid={`${testid}-display`}
              className={`block min-h-[3.5rem] rounded-xl border-2 px-4 py-2 text-right font-mono text-3xl tabular-nums ${
                bad ? 'border-danger-500 bg-danger-50 text-danger-700' : 'border-line bg-surface text-content'}`}>
        {value || ' '}
      </output>
      {hint && <p className={`text-sm ${bad ? 'font-medium text-danger-700' : 'text-content-muted'}`}
                  data-testid={`${testid}-hint`}>{hint}</p>}
      <div className="grid grid-cols-3 gap-3" role="group" aria-label={label}>
        {NUM_KEYS.map((k) => (
          <button key={k} type="button" aria-label={k === 'back' ? 'Delete last digit' : k === '.' ? 'Decimal point' : k}
                  data-testid={`${testid}-key-${k}`} onClick={() => onChange(pressNumber(value, k))}
                  className="flex h-14 min-h-[3.5rem] items-center justify-center rounded-xl border border-line bg-surface text-2xl font-semibold text-content hover:bg-surface-subtle">
            {k === 'back' ? <BackspaceIcon className="h-7 w-7" aria-hidden="true" /> : k}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <button type="button" data-testid={`${testid}-key-minus`} onClick={() => onChange(pressNumber(value, '-'))}
                className="min-h-[3rem] rounded-xl border border-line bg-surface-subtle text-base font-medium text-content">
          − / +
        </button>
        <button type="button" data-testid={`${testid}-key-clear`} onClick={() => onChange('')}
                className="min-h-[3rem] rounded-xl border border-line bg-surface-subtle text-base font-medium text-content">
          Clear
        </button>
        <button type="button" data-testid={`${testid}-done`} onClick={onDone}
                className="btn-primary min-h-[3rem] text-base">
          Done
        </button>
      </div>
    </div>
  );
}
